import { useEffect, useRef, useState } from 'react'
import Guacamole from 'guacamole-common-js'

// Token fetch is proxied through Vite (same-origin, sidesteps CORS +
// SuperTokens' global fetch wrapper). WebSockets don't have CORS
// preflights so we hit the bridge directly. CSP `connect-src ws://*`
// already allows it.
const TOKEN_URL = import.meta.env.VITE_RDP_TOKEN_URL ?? '/rdp-token'
const WS_URL = import.meta.env.VITE_RDP_WS_URL ?? 'ws://127.0.0.1:8090'

const STATE_LABELS = [
  'idle',
  'connecting',
  'waiting',
  'connected',
  'disconnecting',
  'disconnected'
]

export const RdpClient: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [status, setStatus] = useState('connecting')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    let client: any
    let keyboard: any
    let resizeObserver: ResizeObserver | null = null
    let sendSizeTimer: ReturnType<typeof setTimeout> | null = null
    let mouseDisplayEl: HTMLElement | null = null
    let trackNative: ((e: Event) => void) | null = null
    let clipboardListener: (() => void) | null = null
    let mousedownClipboardListener: ((e: MouseEvent) => void) | null = null
    let displayElForCleanup: HTMLElement | null = null
    // Track the last clipboard value we synced in either direction so we
    // don't bounce the same string back and forth between browser and RDP.
    let lastSyncedClipboard = ''

    ;(async () => {
      try {
        const res = await fetch(TOKEN_URL, { credentials: 'include' })
        if (!res.ok) throw new Error(`token endpoint: ${res.status}`)
        const { token } = await res.json()
        if (cancelled) return

        const container = containerRef.current
        if (!container) return

        const tunnel = new Guacamole.WebSocketTunnel(WS_URL)
        client = new Guacamole.Client(tunnel)

        const display = client.getDisplay()
        const displayEl = display.getElement() as HTMLElement
        container.innerHTML = ''
        container.appendChild(displayEl)

        // Force own GPU compositing layer — without this, Chromium draws to the
        // canvas pixel buffer correctly but never paints to screen. Discovered
        // during the PoC.
        displayEl.style.transform = 'translateZ(0)'
        const inner = displayEl.children[0] as HTMLElement | undefined
        if (inner) inner.style.transform = 'translateZ(0)'

        // CSS-fit: scale the display element so the whole desktop is visible
        // inside the container. Called from display.onresize (server confirmed
        // new canvas size) — never calls sendSize() to avoid a resize loop.
        const fit = () => {
          const c = containerRef.current
          if (!c) return
          const dw = display.getWidth()
          const dh = display.getHeight()
          if (!dw || !dh || !c.clientWidth || !c.clientHeight) return
          const scale = Math.min(c.clientWidth / dw, c.clientHeight / dh)
          display.scale(scale)
        }

        // Ask the RDP server to render at the container's pixel resolution.
        // Debounced at 300 ms — rapid consecutive sendSize() calls can crash
        // guacd (GUACAMOLE-1926). Never called from display.onresize to avoid
        // a feedback loop (server resize → onresize → sendSize → server resize…).
        const requestNativeSize = () => {
          const c = containerRef.current
          if (!c || !c.clientWidth || !c.clientHeight) return
          if (sendSizeTimer) clearTimeout(sendSizeTimer)
          sendSizeTimer = setTimeout(() => {
            client?.sendSize(c.clientWidth, c.clientHeight)
            sendSizeTimer = null
          }, 300)
        }

        // display.onresize fires when the server confirms a new canvas size.
        // Only re-fit the CSS scale here — do NOT call sendSize().
        display.onresize = () => fit()

        // Container resize → debounced sendSize so the RDP stream follows.
        resizeObserver = new ResizeObserver(() => { fit(); requestNativeSize() })
        resizeObserver.observe(container)

        client.onstatechange = (state: number) => {
          setStatus(STATE_LABELS[state] ?? `state ${state}`)
          // STATE 3 = CONNECTED. Send native resolution once so the RDP server
          // renders at container size from the first frame rather than its
          // negotiated default (often 800×600 or 1024×768).
          if (state === 3) requestNativeSize()
        }
        client.onerror = (s: { code: number; message?: string }) => {
          setError(`${s.code}: ${s.message ?? 'connection error'}`)
        }

        // Pass initial dimensions in the connect string so guacd can hint the
        // RDP server at handshake time, before sendSize() fires post-connect.
        const w = container.clientWidth || 1280
        const h = container.clientHeight || 800
        client.connect(`token=${encodeURIComponent(token)}&width=${w}&height=${h}`)

        // fromClientPosition() uses offsetLeft/offsetTop which doesn't account
        // for flexbox centering of the bounds element inside our container.
        // The centering creates a Y-offset: small enough that top-area icons still
        // hit, but large enough to miss the taskbar (bottom of screen).
        // Fix: capture native clientX/Y in the capture phase, then use
        // getBoundingClientRect() for accurate position regardless of layout.
        mouseDisplayEl = displayEl
        let nativeX = 0, nativeY = 0
        trackNative = (e: Event) => {
          const me = e as MouseEvent
          nativeX = me.clientX
          nativeY = me.clientY
        }
        displayEl.addEventListener('mousemove', trackNative, true)
        displayEl.addEventListener('mousedown', trackNative, true)
        displayEl.addEventListener('mouseup', trackNative, true)

        const mouse = new Guacamole.Mouse(displayEl)
        mouse.onEach(['mousedown', 'mousemove', 'mouseup'], (e: any) => {
          const rect = displayEl.getBoundingClientRect()
          const scale = display.getScale() || 1
          client.sendMouseState({
            x: Math.round((nativeX - rect.left) / scale),
            y: Math.round((nativeY - rect.top) / scale),
            left: e.state.left,
            middle: e.state.middle,
            right: e.state.right,
            up: e.state.up,
            down: e.state.down,
          })
        })

        // Attach keyboard to document — container-scoped keyboard never
        // receives events because Guacamole.Mouse stops propagation on
        // displayEl, preventing mousedown from reaching the container and
        // focusing it. Guard against forwarding keystrokes while the user
        // is typing in a native input (e.g. the chat overlay text field).
        keyboard = new Guacamole.Keyboard(container)
        const isTypingInInput = () => {
          const el = document.activeElement
          if (!el) return false
          const tag = (el as HTMLElement).tagName
          return tag === 'INPUT' || tag === 'TEXTAREA' || (el as HTMLElement).isContentEditable
        }
        keyboard.onkeydown = (keysym: number) => {
          if (isTypingInInput()) return
          client.sendKeyEvent(1, keysym)
        }
        keyboard.onkeyup = (keysym: number) => {
          if (isTypingInInput()) return
          client.sendKeyEvent(0, keysym)
        }

        // ---- Clipboard sync ----------------------------------------------
        // RDP → browser: when something is copied inside the desktop, guacd
        // sends a clipboard stream. We read it and push to the browser's
        // local clipboard so the user can paste it elsewhere.
        client.onclipboard = (stream: any, mimetype: string) => {
          if (!mimetype.startsWith('text/')) return
          const reader = new Guacamole.StringReader(stream)
          let received = ''
          reader.ontext = (text: string) => {
            received += text
          }
          reader.onend = () => {
            lastSyncedClipboard = received
            navigator.clipboard.writeText(received).catch((e) => {
              // Browsers require focus to write the clipboard. Not fatal.
              console.debug('[rdp] clipboard.writeText skipped:', e?.message)
            })
          }
        }

        // Browser → RDP: push the local clipboard into the RDP session on
        // window focus (handles Cmd+Tab back into the lab) and on
        // mousedown inside the RDP display (handles copy-from-chat-then-
        // click-into-RDP, where focus never changes). BOTH listeners are
        // gated on the clipboard-read permission being 'granted' — with
        // a persistent grant, readText() is silent. Without it, the
        // listeners no-op so clicks pass straight through to the RDP
        // canvas instead of being intercepted by Chrome's floating
        // "Paste" affordance. The grant is primed off-canvas by the
        // Launch button click (see LaunchCard.tsx) so users never see
        // the prompt land over the desktop.
        const pushLocalClipboardToRdp = async () => {
          if (!navigator.clipboard?.readText) return
          try {
            const text = await navigator.clipboard.readText()
            if (!text || text === lastSyncedClipboard) return
            lastSyncedClipboard = text
            const stream = client.createClipboardStream('text/plain')
            const writer = new Guacamole.StringWriter(stream)
            writer.sendText(text)
            writer.sendEnd()
          } catch {
            // Permission revoked or read failed — ignore silently.
          }
        }

        let clipboardReadGranted = false
        try {
          const perm = await navigator.permissions?.query?.({
            name: 'clipboard-read' as PermissionName,
          })
          if (perm) {
            clipboardReadGranted = perm.state === 'granted'
            perm.onchange = () => {
              clipboardReadGranted = perm.state === 'granted'
            }
          }
        } catch {
          // Firefox/Safari don't expose clipboard-read in permissions.query
          // — leave clipboardReadGranted false; host→RDP paste won't sync,
          // but the rest of the session works.
        }

        clipboardListener = () => {
          if (clipboardReadGranted) void pushLocalClipboardToRdp()
        }
        mousedownClipboardListener = () => {
          if (clipboardReadGranted) void pushLocalClipboardToRdp()
        }
        window.addEventListener('focus', clipboardListener)
        // Capture phase so we run before guacamole-common-js's mouse
        // handler stops/preventsDefault the event.
        displayEl.addEventListener('mousedown', mousedownClipboardListener, true)
        displayElForCleanup = displayEl
      } catch (e: any) {
        setError(e?.message ?? String(e))
      }
    })()

    return () => {
      cancelled = true
      if (sendSizeTimer) clearTimeout(sendSizeTimer)
      resizeObserver?.disconnect()
      if (mouseDisplayEl && trackNative) {
        mouseDisplayEl.removeEventListener('mousemove', trackNative, true)
        mouseDisplayEl.removeEventListener('mousedown', trackNative, true)
        mouseDisplayEl.removeEventListener('mouseup', trackNative, true)
      }
      if (keyboard) {
        keyboard.onkeydown = null
        keyboard.onkeyup = null
      }
      if (clipboardListener) {
        window.removeEventListener('focus', clipboardListener)
      }
      if (mousedownClipboardListener && displayElForCleanup) {
        displayElForCleanup.removeEventListener(
          'mousedown',
          mousedownClipboardListener,
          true
        )
      }
      try {
        client?.disconnect()
      } catch {
        // ignore
      }
    }
  }, [])

  return (
    <div className="relative w-full h-full">
      <div
        ref={containerRef}
        data-lab-rdp-container=""
        className="w-full h-full overflow-hidden flex items-center justify-center"
        style={{ cursor: 'none', background: '#1c1712', outline: 'none' }}
        tabIndex={0}
        onMouseDown={() => containerRef.current?.focus()}
      />
      <div
        className="absolute top-2 right-2 px-2.5 py-1 rounded text-[11px] font-mono"
        style={{ background: 'rgba(28,23,18,0.75)', color: '#ffffff' }}
      >
        {error ? `error · ${error}` : `status · ${status}`}
      </div>
    </div>
  )
}
